import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UserManagementTable = ({ members, roles, onAssignRole, onRevokeRole, onToggleStatus }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Get unique departments
  const departments = ['all', ...new Set(members?.map(m => m?.department)?.filter(Boolean))];

  const filteredMembers = members?.filter(member => {
    const matchesSearch = 
      member?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.employeeNumber?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      member?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesDepartment = departmentFilter === 'all' || member?.department === departmentFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && member?.isActive) ||
      (statusFilter === 'inactive' && !member?.isActive);

    return matchesSearch && matchesDepartment && matchesStatus;
  }) || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
        <div className="flex flex-wrap gap-2">
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e?.target?.value)}
            className="px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {departments?.map(dept => (
              <option key={dept} value={dept}>
                {dept === 'all' ? 'All Departments' : dept}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="relative w-full md:w-80">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, email, or employee number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-muted-foreground">
        Showing {filteredMembers?.length} of {members?.length} team members
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Roles</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Last Login</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers?.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-muted-foreground">
                  <Icon name="Users" size={48} className="mx-auto mb-2 opacity-50" />
                  <p>No team members found</p>
                </td>
              </tr>
            ) : (
              filteredMembers?.map((member) => (
                <tr key={member?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-semibold text-primary">
                          {member?.firstName?.[0]}{member?.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{member?.fullName}</p>
                        <p className="text-xs text-muted-foreground">{member?.email}</p>
                        <p className="text-xs text-muted-foreground">#{member?.employeeNumber}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-sm text-foreground">{member?.department || 'Unassigned'}</p>
                      <p className="text-xs text-muted-foreground">{member?.position}</p>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-1">
                      {member?.roles?.length === 0 ? (
                        <span className="text-xs text-muted-foreground">No roles assigned</span>
                      ) : (
                        member?.roles?.map((role) => (
                          <div
                            key={role?.assignmentId}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary"
                          >
                            <Icon name="Shield" size={10} />
                            {role?.roleName}
                            <button
                              onClick={() => onRevokeRole?.(role?.assignmentId)}
                              className="ml-1 hover:text-error transition-colors"
                              title="Revoke role"
                            >
                              <Icon name="X" size={10} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <p className="text-sm text-foreground">{formatDate(member?.lastLogin)}</p>
                  </td>
                  <td className="py-4 px-4">
                    {member?.isActive ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        <Icon name="CheckCircle" size={12} className="mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                        <Icon name="XCircle" size={12} className="mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAssignRole?.(member)}
                        title="Assign role"
                      >
                        <Icon name="UserPlus" size={14} />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onToggleStatus?.(member?.id, member?.isActive)}
                        title={member?.isActive ? 'Deactivate' : 'Activate'}
                      >
                        <Icon name={member?.isActive ? 'UserX' : 'UserCheck'} size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagementTable;